import React, { useState } from 'react';
import { X, CreditCard, Building2, Smartphone, Bitcoin, DollarSign } from 'lucide-react';
import { useAccount } from '../contexts/AccountContext';
import { useTheme } from '../contexts/ThemeContext';

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DepositModal: React.FC<DepositModalProps> = ({ isOpen, onClose }) => {
  const { deposit, isLoading } = useAccount();
  const { theme } = useTheme();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [step, setStep] = useState<'amount' | 'method' | 'confirmation'>('amount');

  const predefinedAmounts = [10, 100, 500, 1000, 5000, 10000];

  const paymentMethods = [
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: Building2,
      description: 'Direct bank transfer',
      color: 'bg-blue-600',
    },
    {
      id: 'upi',
      name: 'UPI',
      icon: Smartphone,
      description: 'Instant UPI transfer',
      color: 'bg-purple-600',
    },
    {
      id: 'skrill',
      name: 'Skrill',
      icon: CreditCard,
      description: 'Digital wallet',
      color: 'bg-orange-600',
    },
    {
      id: 'stripe',
      name: 'Stripe',
      icon: CreditCard,
      description: 'Credit/Debit card',
      color: 'bg-indigo-600',
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: CreditCard,
      description: 'PayPal account',
      color: 'bg-blue-500',
    },
    {
      id: 'crypto',
      name: 'Cryptocurrency',
      icon: Bitcoin,
      description: 'Bitcoin, Ethereum, etc.',
      color: 'bg-yellow-600',
    },
  ];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount(amount.toString());
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
  };

  const handleNext = () => {
    if (step === 'amount') {
      const amount = selectedAmount || parseFloat(customAmount);
      if (amount && amount >= 10) {
        setStep('method');
      }
    } else if (step === 'method') {
      if (selectedMethod) {
        setStep('confirmation');
      }
    }
  };

  const handleBack = () => {
    if (step === 'method') {
      setStep('amount');
    } else if (step === 'confirmation') {
      setStep('method');
    }
  };

  const handleConfirmDeposit = async () => {
    try {
      const amount = selectedAmount || parseFloat(customAmount);
      const method = paymentMethods.find(m => m.id === selectedMethod)?.name || selectedMethod;
      
      if (amount && method) {
        await deposit(amount, method);
        onClose();
        // Reset form
        setSelectedAmount(null);
        setCustomAmount('');
        setSelectedMethod('');
        setStep('amount');
      }
    } catch (error) {
      console.error('Deposit failed:', error);
    }
  };

  const getCurrentAmount = () => {
    return selectedAmount || parseFloat(customAmount) || 0;
  };

  const canProceed = () => {
    if (step === 'amount') {
      const amount = selectedAmount || parseFloat(customAmount);
      return amount && amount >= 10;
    } else if (step === 'method') {
      return selectedMethod !== '';
    }
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Modal */}
      <div className={`relative rounded-lg shadow-xl w-full max-w-md mx-4 ${
        theme === 'light' ? 'bg-white' : 'bg-gray-800'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          theme === 'light' ? 'border-gray-200' : 'border-gray-700'
        }`}>
          <h2 className={`text-xl font-semibold ${
            theme === 'light' ? 'text-gray-900' : 'text-gray-100'
          }`}>Deposit Funds</h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'amount' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                1
              </div>
              <div className={`w-8 h-1 ${
                theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'method' ? 'bg-[#2AB7A6] text-black' : step === 'confirmation' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                2
              </div>
              <div className={`w-8 h-1 ${
                theme === 'light' ? 'bg-gray-300' : 'bg-gray-600'
              }`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'confirmation' ? 'bg-[#2AB7A6] text-black' : theme === 'light' ? 'bg-gray-300 text-gray-700' : 'bg-gray-600 text-gray-300'
              }`}>
                3
              </div>
            </div>
          </div>

                     {/* Step 1: Amount Selection */}
           {step === 'amount' && (
             <div>
                               <h3 className={`text-xs font-medium mb-4 ${
                                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                               }`}>Select Deposit Amount</h3>
               
               {/* Predefined Amounts */}
               <div className="grid grid-cols-3 gap-3 mb-6">
                 {predefinedAmounts.map((amount) => (
                   <button
                     key={amount}
                     onClick={() => handleAmountSelect(amount)}
                     className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                       selectedAmount === amount
                         ? 'border-[#2AB7A6] bg-[#2AB7A6] text-black'
                         : theme === 'light' 
                           ? 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50' 
                           : 'border-gray-600 text-gray-300 hover:border-gray-500'
                     }`}
                   >
                                           <div className="text-xs font-bold">${amount.toLocaleString()}</div>
                   </button>
                 ))}
               </div>

               {/* Custom Amount */}
               <div>
                 <label className={`block text-xs font-medium mb-2 ${
                   theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                 }`}>
                   Or enter custom amount (min $10)
                 </label>
                 <div className="relative">
                   <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
                     theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                   }`} />
                   <input
                     type="number"
                     value={customAmount}
                     onChange={(e) => handleCustomAmountChange(e.target.value)}
                     placeholder="0.00"
                     min="10"
                     step="0.01"
                     className={`w-full pl-10 pr-4 py-3 border rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#2AB7A6] focus:border-transparent ${
                       theme === 'light' 
                         ? 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400' 
                         : 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500'
                     }`}
                   />
                 </div>
               </div>
             </div>
           )}

                     {/* Step 2: Payment Method Selection */}
           {step === 'method' && (
             <div>
               <h3 className={`text-lg font-medium mb-4 ${
                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
               }`}>Select Payment Method</h3>
               
                               <div className="max-h-64 overflow-y-auto space-y-3 pr-2">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        onClick={() => handleMethodSelect(method.id)}
                        className={`w-full p-4 rounded-lg transition-all duration-200 text-left ${
                          selectedMethod === method.id
                            ? 'border-[#2AB7A6] bg-[#2AB7A6] text-black'
                            : theme === 'light' 
                              ? 'border !border-gray-300 hover:!border-[#2AB7A6] hover:bg-gray-50' 
                              : 'border-2 border-gray-600 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${method.color}`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className={`text-xs font-medium ${
                              theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                            }`}>{method.name}</div>
                            <div className={`text-xs ${
                              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                            }`}>{method.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
             </div>
           )}

                     {/* Step 3: Confirmation */}
           {step === 'confirmation' && (
             <div>
               <h3 className={`text-lg font-medium mb-4 ${
                 theme === 'light' ? 'text-gray-900' : 'text-gray-100'
               }`}>Confirm Deposit</h3>
               
               <div className={`rounded-lg p-4 mb-6 ${
                 theme === 'light' ? 'bg-gray-50 border border-gray-200' : 'bg-gray-700'
               }`}>
                 <div className="space-y-3">
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Amount:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>${getCurrentAmount().toLocaleString()}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Payment Method:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>
                       {paymentMethods.find(m => m.id === selectedMethod)?.name}
                     </span>
                   </div>
                   <div className="flex justify-between">
                     <span className={`text-xs ${
                       theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                     }`}>Processing Fee:</span>
                     <span className={`text-xs font-medium ${
                       theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                     }`}>$0.00</span>
                   </div>
                   <div className={`border-t pt-3 ${
                     theme === 'light' ? 'border-gray-200' : 'border-gray-600'
                   }`}>
                     <div className="flex justify-between">
                       <span className={`text-xs font-medium ${
                         theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                       }`}>Total:</span>
                       <span className="text-[#2AB7A6] font-bold text-lg">${getCurrentAmount().toLocaleString()}</span>
                     </div>
                   </div>
                 </div>
               </div>

               <div className={`text-xs text-center ${
                 theme === 'light' ? 'text-gray-600' : 'text-gray-400'
               }`}>
                 Click confirm to proceed to {paymentMethods.find(m => m.id === selectedMethod)?.name} for payment processing.
               </div>
             </div>
           )}

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6">
            {step !== 'amount' && (
              <button
                onClick={handleBack}
                className={`flex-1 px-4 py-3 rounded-lg transition-colors ${
                  theme === 'light' 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                Back
              </button>
            )}
            
            {step !== 'confirmation' ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 bg-[#2AB7A6] text-white hover:bg-[#25a595] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleConfirmDeposit}
                disabled={isLoading}
                className="flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors duration-200 bg-[#2AB7A6] text-white hover:bg-[#25a595] disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Confirm Deposit'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositModal;
